import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { XDrawerBottom } from '../../x/drawer-bottom/XDrawerBottom';
import { useToggle } from '../../../hooks/toggle';
import { UiText, UiGrid, UiFlex, UiButton, UiBox } from '@open-stack/ui';
import { useCovidRegistration } from '../../../hooks/covid-registration';
import { XTranslate } from '../../x/translate/XTranslate';
import { XInputPhone } from '../../x/input/phone/XInputPhone';
import { XDrawerBottomHeader } from '../../x/drawer-bottom/header/XDrawerBottomHeader';
import { termsAndConditions } from './terms';
import { useApiQuery } from '@orderx/lab/xr/http';
import { Outlet } from '../../../XV2';
import { XAlertType, XAlert } from '../../x/alert/XAlert';
import { XText } from '../../x/text/XText';
import { XInput } from '../../x/input/XInput';
import { XButtonBottom } from '../../x/button/bottom/XButtonBottom';
import { Zone } from '../../../XV2';
import { useImmer } from 'use-immer';
import flatMap from 'lodash/flatMap';

enum ViewState {
    INITIAL = 0,
    REGISTRATION_REQUIRED = 1,
    REGISTERED = 2,
}

const Wrapper = styled.div`
    > .overlay {
        position: fixed;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        z-index: var(--z-index-covid-reg-form);
    }

    .underline {
        text-decoration: underline !important;
    }
`;

interface Props {
    outlet: Outlet;
    zone?: Zone;
    tagData?: {
        outlets: {
            id: number;
            name: string;
            zones: {
                id: number;
                name: string;
            }[];
        }[];
    };
}

export function CovidRegistrationOverlay({ outlet, zone, tagData }: Props) {
    const [viewState, setViewState] = useState(ViewState.INITIAL);
    const [isTermsOpen, toggleIsTermsOpen] = useToggle();
    const { form, updateForm, isAutoRegister, setIsAutoRegister, queryRegister, initialAutoRegister } =
        useCovidRegistration();
    const [isConfirmed, toggleIsConfirmed] = useToggle(initialAutoRegister);
    const { isCovidRegistrationRequiredUpfront } = outlet;

    const [tagForm, setTagForm] = useImmer<{
        outletId: number | undefined;
        outletName: string | undefined;
        zoneId: number | undefined;
        zoneName: string | undefined;
    }>({
        outletId: undefined,
        outletName: undefined,
        zoneId: undefined,
        zoneName: undefined,
    });

    const isOutletSelectRequired = (tagData?.outlets || []).length > 0;
    const isValidForm =
        form.name &&
        form.areaCode &&
        form.phone &&
        isConfirmed &&
        (!isOutletSelectRequired || (isOutletSelectRequired && tagForm.zoneId));

    function getToggleButton(isActive) {
        return (
            <UiButton
                color={isActive ? 'success' : 'default'}
                variant="text"
                icon={isActive ? 'check_box' : 'check_box_outline_blank'}
            />
        );
    }

    async function register() {
        try {
            await queryRegister.run({
                outletId: tagForm.outletId ?? outlet.id,
                outletName: tagForm.outletName ?? outlet.name,
                zoneId: tagForm.zoneId ?? zone?.id,
                zoneName: tagForm.zoneName ?? zone?.name,
            });

            toggleIsConfirmed(false);
            setViewState(ViewState.REGISTERED);
        } catch (error) {
            //toastShowError('Failed to register');
            setViewState(ViewState.INITIAL);
        }
    }

    const queryTryRegister = useApiQuery(async () => {
        if (initialAutoRegister && isValidForm) {
            await register();
            setViewState(ViewState.REGISTERED);
        } else {
            setViewState(ViewState.REGISTRATION_REQUIRED);
        }
    });

    useEffect(() => {
        if (isCovidRegistrationRequiredUpfront || (initialAutoRegister && isValidForm)) {
            queryTryRegister.run();
        } else {
            setViewState(ViewState.INITIAL);
        }
    }, [initialAutoRegister, isCovidRegistrationRequiredUpfront]);

    if (queryTryRegister.isLoading || viewState === ViewState.REGISTERED) {
        return null;
    }

    return (
        <Wrapper>
            {viewState === ViewState.REGISTRATION_REQUIRED ? (
                <XDrawerBottom onClose={() => setViewState(ViewState.INITIAL)}>
                    <Wrapper>
                        <UiGrid padding={2}>
                            <XAlert type={XAlertType.WARNING} padding={2}>
                                <XText>
                                    <XTranslate id="covid_reg.description" />
                                </XText>
                            </XAlert>

                            <div />

                            <XInput
                                value={form.name}
                                onChangeValue={(name) => updateForm({ name })}
                                label="Name"
                                fullWidth
                            />

                            <XInputPhone areaCode={form.areaCode} phone={form.phone} onChange={updateForm} />

                            {isOutletSelectRequired && (
                                <div>
                                    <XInput
                                        label="Choose zone"
                                        value={form.zoneId}
                                        onChangeValue={(zoneId) => {
                                            for (const outlet of tagData?.outlets || []) {
                                                for (const zone of outlet.zones || []) {
                                                    if (zone.id === zoneId) {
                                                        updateForm({
                                                            outletId: outlet.id,
                                                            outletName: outlet.name,
                                                            zoneId: zone.id,
                                                            zoneName: zone.name,
                                                        });

                                                        setTagForm(() => ({
                                                            outletId: outlet.id,
                                                            outletName: outlet.name,
                                                            zoneId: zone.id,
                                                            zoneName: zone.name,
                                                        }));
                                                    }
                                                }
                                            }
                                        }}
                                        options={flatMap(tagData?.outlets, (outlet) =>
                                            outlet.zones.map((zone) => ({
                                                label: `${outlet.name} - ${zone.name}`,
                                                value: zone.id,
                                            })),
                                        )}
                                    />
                                </div>
                            )}

                            <UiFlex onClick={() => setIsAutoRegister(!isAutoRegister)} gap={2}>
                                {getToggleButton(isAutoRegister)}
                                <UiText>Auto check-in into other venues</UiText>
                            </UiFlex>

                            <UiFlex onClick={toggleIsConfirmed} gap={2}>
                                {getToggleButton(isConfirmed)}

                                <UiText>
                                    I confirm I don't have any symptoms of COVID-19 &{' '}
                                    <span
                                        className="underline"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            toggleIsTermsOpen();
                                        }}>
                                        I agree to terms
                                    </span>{' '}
                                    for my contact information being stored for the next 14 days.
                                </UiText>
                            </UiFlex>

                            <XButtonBottom
                                onClick={register}
                                isLoading={queryRegister.isLoading}
                                disabled={!isValidForm}
                                color="primary"
                                hasPlaceholder>
                                Confirm
                            </XButtonBottom>
                        </UiGrid>
                    </Wrapper>
                </XDrawerBottom>
            ) : (
                <div onClick={queryTryRegister.run} className="overlay" />
            )}

            {isTermsOpen && (
                <XDrawerBottom onClose={toggleIsTermsOpen}>
                    <XDrawerBottomHeader title="Terms and conditions" onClose={toggleIsTermsOpen} />
                    <UiBox padding={2} dangerouslySetInnerHTML={{ __html: termsAndConditions }} />
                </XDrawerBottom>
            )}
        </Wrapper>
    );
}
